import React, { useState, useEffect } from 'react';
import { EXPENSE_CATEGORIES, BANKS } from '../constants';

const TransactionForm = ({ partners, onTransactionAdded, onCancel, exchangeRate }) => {
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    amountGBP: '',
    exchangeRate: exchangeRate || 365,
    description: '',
    category: '',
    partner_id: '',
    bankId: 'alfalah',
    transferFrom: 'alfalah',
    transferTo: 'meezan'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update exchange rate when prop changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      exchangeRate: exchangeRate || 365
    }));
  }, [exchangeRate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const setType = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      amount: '',
      amountGBP: ''
    }));
    setError('');
  };

  // Reset amounts when bank changes to prevent stale data
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      amount: '',
      amountGBP: ''
    }));
  }, [formData.bankId, formData.transferFrom, formData.transferTo]);

  const isPayoneer = formData.bankId === 'payoneer' || (formData.type === 'transfer' && (formData.transferFrom === 'payoneer' || formData.transferTo === 'payoneer'));
  const calculatedPKR = isPayoneer && formData.amountGBP && formData.exchangeRate
    ? (parseFloat(formData.amountGBP) * parseFloat(formData.exchangeRate)).toFixed(2)
    : '0';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (isPayoneer) {
      if (!formData.amountGBP || !formData.exchangeRate) {
        setError('GBP amount and exchange rate are required for Payoneer transactions');
        setLoading(false);
        return;
      }
    } else if (!formData.amount) {
      setError('Amount is required');
      setLoading(false);
      return;
    }

    if (!formData.description) {
      setError('Description is required');
      setLoading(false);
      return;
    }

    if ((formData.type === 'loan_taken' || formData.type === 'loan_repayment') && !formData.partner_id) {
      setError('Please select a founder for loan transactions');
      setLoading(false);
      return;
    }

    if (formData.type === 'transfer') {
      if (!formData.transferFrom || !formData.transferTo || formData.transferFrom === formData.transferTo) {
        setError('Please select two different banks for transfer');
        setLoading(false);
        return;
      }
    }

    try {
      const transactionData = {
        ...formData,
        partner_id: formData.partner_id || null
      };

      if (isPayoneer) {
        transactionData.amountGBP = parseFloat(formData.amountGBP);
        transactionData.exchangeRate = parseFloat(formData.exchangeRate);
        transactionData.amount = transactionData.amountGBP * transactionData.exchangeRate;
      } else {
        transactionData.amount = parseFloat(formData.amount);
      }

      console.log('Final Transaction Data being submitted:', transactionData);

      await onTransactionAdded(transactionData);
      onCancel();
    } catch (error) {
      setError('Failed to add transaction. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { id: 'expense', label: 'Expense', icon: '💸', color: 'var(--danger)' },
    { id: 'income', label: 'Income', icon: '💰', color: 'var(--success)' },
    { id: 'loan_taken', label: 'Loan Taken', icon: '💳', color: 'var(--warning)' },
    { id: 'loan_repayment', label: 'Loan Repayment', icon: '🤝', color: 'var(--success)' },
    { id: 'transfer', label: 'Transfer', icon: '🔁', color: 'var(--text-primary)' },
  ];

  return (
    <div className="fade-in" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div className="card">
        <h2 className="text-2xl font-bold mb-6 text-yellow">➕ Add New Transaction</h2>

        {error && (
          <div className="error-message mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Visual Type Selector */}
          <div className="mb-8">
            <label className="form-label mb-3">Transaction Type</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {typeOptions.map(opt => (
                <div
                  key={opt.id}
                  onClick={() => setType(opt.id)}
                  className={`stat-card cursor-pointer transition-all ${formData.type === opt.id ? 'active-type' : ''}`}
                  style={{
                    padding: '0.75rem',
                    borderWidth: '2px',
                    borderColor: formData.type === opt.id ? opt.color : 'var(--border)',
                    background: formData.type === opt.id ? `${opt.color}11` : 'var(--secondary-bg)'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{opt.icon}</div>
                  <div className="text-xs font-bold">{opt.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {formData.type !== 'transfer' && (
                <div className="form-group mb-0">
                  <label className="form-label">Bank Account</label>
                  <select
                    name="bankId"
                    value={formData.bankId}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    {BANKS.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.type === 'transfer' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group mb-0">
                    <label className="form-label">From</label>
                    <select
                      name="transferFrom"
                      value={formData.transferFrom}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {BANKS.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group mb-0">
                    <label className="form-label">To</label>
                    <select
                      name="transferTo"
                      value={formData.transferTo}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {BANKS.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="form-group mb-0">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="What was this for?"
                  required
                />
              </div>

              {formData.type === 'expense' && (
                <div className="form-group mb-0">
                  <label className="form-label">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select category...</option>
                    {EXPENSE_CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              )}

              {(formData.type === 'loan_taken' || formData.type === 'loan_repayment') && (
                <div className="form-group mb-0">
                  <label className="form-label">Founder</label>
                  <select
                    name="partner_id"
                    value={formData.partner_id}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select founder...</option>
                    {partners.map(partner => (
                      <option key={partner.id} value={partner.id}>{partner.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Right Column - Amount Section */}
            <div className="space-y-4">
              {isPayoneer ? (
                <div className="card" style={{ background: 'rgba(255,255,255,0.03)', borderStyle: 'dashed' }}>
                  <div className="form-group">
                    <label className="form-label">Amount (GBP)</label>
                    <input
                      type="number"
                      name="amountGBP"
                      value={formData.amountGBP}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="0.00"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Exchange Rate</label>
                    <input
                      type="number"
                      name="exchangeRate"
                      value={formData.exchangeRate}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="365.0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="p-3 rounded bg-secondary text-center">
                    <p className="text-xs text-muted mb-1">PKR Equivalent</p>
                    <p className="text-xl font-bold text-yellow">{calculatedPKR} PKR</p>
                  </div>
                </div>
              ) : (
                <div className="form-group mb-0">
                  <label className="form-label">Amount (PKR)</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className="form-input"
                    style={{ fontSize: '1.5rem', fontWeight: 'bold', height: '60px' }}
                    placeholder="0"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ flex: 2, height: '50px' }}
            >
              {loading ? (
                <><div className="spinner"></div> Processing...</>
              ) : (
                '✅ Save Transaction'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              style={{ flex: 1 }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .active-type {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        }
        .stat-card.cursor-pointer:hover {
          border-color: var(--accent-yellow);
        }
      `}} />
    </div>
  );
};

export default TransactionForm;